import { Table, TableColumn } from '@wix/design-system';
import React, { FC, useCallback, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { logSearchResultsPresented } from '../../bi';
import { SearchArticlesItem } from '../../common/api/searchArticles';
import { Loader } from '../../common/components/Loader';
import { ScrollBox } from '../../common/components/ScrollBox';
import { useBiEngagementEventHandlers } from '../../common/hooks/useBiEngagementEventHandlers';
import { useOnWidgetExpanded } from '../../common/hooks/useOnWidgetExpanded';
import { SEARCH_LIMIT } from '../../common/reducers/solutions';
import { selectSolutions, selectSolutionsPaging } from '../../common/selectors/solutions';
import { experiments } from '../../experiments';
import {
  renderArticlesListItem,
  sharedSearchResultsItem,
} from '../../help-center/components/ArticlesListItem';
import { SolutionsNotReady } from './SolutionNotReady';
import { useFetchSolutions } from './useFetchSolutions';

const isSharedSearchResultsItem = experiments.is('specs.helpwidget.sharedSearchResultsItem', 'true');

const columns: TableColumn<SearchArticlesItem>[] = [
  {
    title: '', // because of titleBarVisible={false}
    style: { padding: 0, boxShadow: 'none' },
    render: isSharedSearchResultsItem ? sharedSearchResultsItem : renderArticlesListItem,
  },
];

export const SolutionList: FC = () => {
  const store = useStore();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const { onScroll, onScrollAddedContent } = useBiEngagementEventHandlers();
  const solutions = useSelector(selectSolutions);
  const paging = useSelector(selectSolutionsPaging);

  const query = searchParams.get('query')!;
  const { fetchSolutions } = useFetchSolutions(query);

  const loadMore = useCallback(() => {
    onScrollAddedContent();
    fetchSolutions();
  }, [fetchSolutions, onScrollAddedContent]);

  useOnWidgetExpanded((expanded: boolean) => {
    if (expanded && solutions && solutions.length > 0) {
      logSearchResultsPresented(store.getState(), solutions);
    }
  });

  const solutionNotReady = <SolutionsNotReady solutions={solutions} query={query} />;
  return (
    <ScrollBox ref={containerRef} onScroll={onScroll} dataHook="solution-list">
      {solutionNotReady}
      {solutions && solutions.length > 0 && (
        <Table
          infiniteScroll
          hasMore={paging.hasMore}
          loadMore={loadMore}
          itemsPerPage={SEARCH_LIMIT}
          data={solutions}
          columns={columns}
          scrollElement={containerRef.current!}
          loader={<Loader dataHook="solution-list__loader" margin="25px" />}
        >
          <Table.Content titleBarVisible={false} />
        </Table>
      )}
    </ScrollBox>
  );
};
